import { useState, useMemo, useEffect } from "react";
import {
  Maximize2,
  Gauge,
  Plane,
  Wind,
  Info,
  ChevronLeft,
  ChevronRight,
  X,
  ShieldCheck,
  Zap,
  PlaneTakeoff,
  TrendingUp,
  MapPin,
  Calendar,
  Hash,
  Globe,
  Users,
  Weight,
  Droplets,
  RotateCcw,
  Settings,
  Banknote,
  Loader2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigation } from "@/context/NavigationContext";
import InstrumentLoader from "@/components/InstrumentLoader";
import { message, Tooltip } from "antd";
import AppConst from "../AppConst";
import { AircraftListingDetailsDto } from "@/types";
import {
  AIRCRAFT_TYPES,
  AIRCRAFT_STATES,
  AIRCRAFT_ORIGIN_COUNTRIES,
  AIRCRAFT_REGISTRATION_COUNTRIES,
  PROPELLER_TYPES,
  ENGINE_STATES,
  ENGINE_COOLINGS
} from "../enums";

const isInvalidValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  const str = String(value).trim().toLowerCase();
  return str === "" || str === "null" || str === "undefined" || str === "n/a" || str === "na";
};

const AircraftProductDetails = () => {
  const { params, navigateTo } = useNavigation();
  const listingId = params?.listingId;
  const aircraftListingFromParams = params?.aircraftListing as any;
  const seller = params?.seller;

  const { aircraftListings, requestViewing, loading, getAircraftListingDetails } = useAppContext();
  const { isAuthenticated, myAircraftListings } = useAuth();

  const [details, setDetails] = useState<AircraftListingDetailsDto | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(true);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [contactMethod, setContactMethod] = useState<"EMAIL" | "PHONE">("EMAIL");

  const isSeller = useMemo(() => {
    const isSellerParam = Number(seller) === 1 || seller === "true" || seller === true;

    const targetId = listingId || aircraftListingFromParams?.listingId;
    const isInMyListings = myAircraftListings && targetId
      ? myAircraftListings.some((myListing: any) => Number(myListing.listingId) === Number(targetId))
      : false;

    return !!(isSellerParam || isInMyListings);
  }, [seller, listingId, aircraftListingFromParams, myAircraftListings]);

  const baseListing = useMemo(() => {
    if (aircraftListingFromParams) return aircraftListingFromParams;
    if (!listingId) return null;
    return aircraftListings.find(l => Number(l.listingId) === Number(listingId)) || null;
  }, [aircraftListings, listingId, aircraftListingFromParams]);

  useEffect(() => {
    const targetId = Number(listingId || baseListing?.listingId);
    if (!targetId) {
      setIsLoadingDetails(false);
      return;
    }

    let isMounted = true;
    setIsLoadingDetails(true);

    getAircraftListingDetails(targetId)
      .then((data) => {
        if (isMounted && data) {
          setDetails(data);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingDetails(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [listingId, baseListing?.listingId, getAircraftListingDetails]);

  const listing = useMemo(() => {
    if (!baseListing) return null;

    const merged = {
      ...baseListing,
      ...(details || {})
    };

    const aircraftTypeMatch = AIRCRAFT_TYPES.flatMap((cat) => cat.options).find(
      (opt) => opt.code === merged.type
    );
    const resolvedTypeLabel = aircraftTypeMatch ? aircraftTypeMatch.label : merged.type;

    const stateMatch = AIRCRAFT_STATES.find((s) => s.code === merged.state);
    const resolvedStateLabel = stateMatch ? stateMatch.label : merged.state;

    const regCountryMatch = AIRCRAFT_REGISTRATION_COUNTRIES.find(
      (c) => c.code === merged.registrationCountry
    );
    const resolvedRegCountryLabel = regCountryMatch ? regCountryMatch.label : merged.registrationCountry;

    const originCountryMatch = AIRCRAFT_ORIGIN_COUNTRIES.find(
      (c) => c.code === merged.originCountry
    );
    const resolvedOriginCountryLabel = originCountryMatch ? originCountryMatch.label : merged.originCountry;

    const propellerTypeMatch = merged.propeller?.type
      ? PROPELLER_TYPES.find((p) => p.code === merged.propeller.type)
      : null;
    const resolvedPropellerLabel = propellerTypeMatch ? propellerTypeMatch.label : merged.propeller?.type;

    const mappedEngines = (merged.engines || []).map((eng: any) => {
      const engineStateMatch = ENGINE_STATES.find((s) => s.code === (eng.state || eng.condition));
      const engineCoolingMatch = ENGINE_COOLINGS.find((c) => c.code === eng.cooling);
      return {
        ...eng,
        resolvedStateLabel: engineStateMatch ? engineStateMatch.label : eng.state,
        resolvedCoolingLabel: engineCoolingMatch ? engineCoolingMatch.label : eng.cooling
      };
    });

    return {
      ...merged,
      resolvedTypeLabel,
      resolvedStateLabel,
      resolvedRegCountryLabel,
      resolvedOriginCountryLabel,
      resolvedPropellerLabel,
      engines: mappedEngines
    };
  }, [baseListing, details]);

  const images = useMemo(() => {
    if (listing?.mediaUrls && listing.mediaUrls.length > 0) {
      return listing.mediaUrls.filter(url => url && url.trim() !== "");
    }

    if (listing?.media && listing.media.length > 0) {
      return listing.media.map((m: any) => {
        const data = m.mediaData;
        if (!data) return "";

        if (data.startsWith("iVBOR")) return `data:image/png;base64,${data}`;
        if (data.startsWith("/9j/")) return `data:image/jpeg;base64,${data}`;

        return data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;
      }).filter((img: string) => img !== "");
    }

    return [`https://via.placeholder.com/1200x800?text=${encodeURIComponent(listing?.model || "Aircraft")}`];
  }, [listing]);

  const financialData = useMemo(() => {
    if (!listing) return null;
    const gross = listing.grossValue ?? 0;
    const isListingPlan = listing.chargingModel === "BY_LISTING_PLAN";
    const rate = isListingPlan ? 0 : (listing.commissionRate ?? 0);
    const commValue = isListingPlan
      ? 0
      : (listing.commissionValue ?? (gross && rate < 1 ? gross * rate : gross * (rate / 100)) ?? 0);
    const net = isListingPlan ? gross : (listing.netValue ?? (gross - commValue));

    return {
      gross,
      rate,
      commValue,
      net,
      isListingPlan,
      chargingModelLabel: isListingPlan ? "Plan Publicare" : "Comision Procentual"
    };
  }, [listing]);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("ro-RO", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(price);

  const isPageLoading = (loading && !aircraftListingFromParams) || isLoadingDetails;

  const dictViewing = {
    title: "Programare Vizionare",
    subtitle: "Confirmă Solicitarea",
    description: "Trimite o cerere de vizionare pentru această aeronavă. Un consultant din echipa noastră te va contacta în cel mai scurt timp pentru a stabili toate detaliile.",
    info: [
      "Vizionările se organizează în funcție de disponibilitatea proprietarului.",
      "Vei primi un răspuns de confirmare direct pe metoda de contact selectată."
    ],
    btnCancel: "Anulează",
    btnConfirm: "Trimite Cererea"
  };

  if (isPageLoading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <InstrumentLoader visible={true} />
      </div>
    );
  }

  if (!listing) return (
    <div className="text-white text-center py-20 font-sans min-h-screen bg-[#020617]">
      <p className="mb-4 text-slate-500 uppercase tracking-widest font-black">Aeronava nu a fost găsită</p>
      <button onClick={() => navigateTo("aircraft")} className="text-orange-500 underline font-bold">
        Înapoi la flotă
      </button>
    </div>
  );

  const handleAction = async () => {
    if (isSeller) {
      navigateTo("list-aircraft", { aircraftListing: listing });
      return;
    }
    if (!isAuthenticated) {
      message.warning("Autentificarea este necesară pentru a programa o vizionare.");
      navigateTo("login");
      return;
    }
    setIsConfirmOpen(true);
  };

  const handleConfirmViewing = async () => {
    setIsConfirmOpen(false);
    setIsRequesting(true);

    const success = await requestViewing({
      ...listing,
      additionalDetails: `${listing.additionalDetails || ""}\n[Preferință Contact Vizionare: ${contactMethod}]`
    });

    if (success) {
      message.success({
        content: `Cererea de vizionare a fost trimisă. Te vom contacta în scurt timp pe ${contactMethod === "EMAIL" ? "E-mail" : "Telefon"}.`,
        duration: AppConst.msgDuration
      });
    } else {
      message.error({
        content: "Cererea de vizionare nu a putut fi trimisă. Încercați mai târziu.",
        duration: AppConst.msgDuration
      });
    }
    setIsRequesting(false);
  };

  const DetailRow = ({ label, value, icon: Icon }: { label: string; value?: string | number | boolean | null; icon?: any }) => {
    if (typeof value !== "boolean" && isInvalidValue(value)) return null;

    return (
      <div className="flex justify-between items-center py-3 border-b border-white/5 group hover:bg-white/[0.02] px-2 transition-colors">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-slate-500" />}
          <span className="text-slate-400 text-[11px] font-bold uppercase tracking-wider">{label}</span>
        </div>
        <span className="text-white font-mono text-sm text-right">
          {typeof value === "boolean" ? (value ? "DA" : "NU") : value}
        </span>
      </div>
    );
  };

  const hasFlightTime = !isInvalidValue(listing.flightTime);
  const hasProductionYear = !isInvalidValue(listing.productionYear);
  const hasAvionics = listing.avionics && listing.avionics.length > 0;
  const hasEquipment = listing.equipment && listing.equipment.length > 0;
  const hasSellerDescription = !isInvalidValue(listing.additionalDetails);

  return (
    <div className="container py-8 text-white min-h-screen lg:h-screen flex flex-col lg:overflow-hidden font-sans bg-[#020617]">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 flex-1 lg:overflow-hidden">
        <div className="lg:col-span-7 h-auto lg:h-full lg:overflow-y-auto no-scrollbar">
          <div className="flex flex-row lg:flex-col rounded-[2rem] overflow-x-auto lg:overflow-hidden snap-x snap-mandatory no-scrollbar border border-white/10 bg-slate-900 shadow-2xl">
            {images.map((src, idx) => (
              <div
                key={idx}
                className="relative min-w-full lg:min-w-0 snap-center group cursor-zoom-in border-r lg:border-r-0 lg:border-b border-white/5 last:border-0"
                onClick={() => {
                  setPhotoIndex(idx);
                  setIsLightboxOpen(true);
                }}
              >
                <img
                  src={src}
                  alt={`Aircraft ${idx}`}
                  className="w-full h-[350px] md:h-[500px] lg:h-auto object-cover transition-transform duration-500 lg:group-hover:scale-[1.01]"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placehold.co/1200x800?text=Image+Unavailable';
                  }}
                />
                <div className="hidden lg:flex absolute inset-0 bg-orange-600/10 opacity-0 group-hover:opacity-100 transition-opacity items-center justify-center">
                  <Maximize2 className="text-white" size={32} />
                </div>
                <div className="lg:hidden absolute bottom-4 right-6 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-mono border border-white/10 text-white">
                  {idx + 1} / {images.length}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 h-auto lg:h-full lg:overflow-y-auto no-scrollbar pb-10 lg:pb-20">
          <div className="bg-[#0F172A]/80 backdrop-blur-xl p-8 rounded-[2.5rem] border border-slate-800 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-600/10 blur-3xl -z-10" />

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-blue-600 text-[9px] font-black rounded uppercase tracking-tighter">Anunț activ</span>
                {!isInvalidValue(listing.registration) && (
                  <span className="text-slate-500 font-mono text-[10px] tracking-widest">{listing.registration}</span>
                )}
              </div>
              <h1 className="text-4xl font-black tracking-tighter mb-1 uppercase italic">
                {listing.manufacturer || listing.builder} <span className="text-slate-500">{listing.model}</span>
              </h1>
              <div className="flex flex-col gap-1">
                {!isInvalidValue(listing.location) && (
                  <p className="text-slate-400 text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                    <MapPin size={14} className="text-orange-500" /> {listing.location}
                  </p>
                )}
                {!isInvalidValue(listing.serialNumber) && (
                  <p className="text-slate-500 text-[10px] font-mono tracking-widest flex items-center gap-2 ml-1">
                    S/N: {listing.serialNumber}
                  </p>
                )}
              </div>
            </header>

            <div className="bg-slate-950/50 border border-slate-800 p-6 rounded-2xl mb-8">
              <div className="flex justify-between items-end mb-4">
                <div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Preț Vânzare</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-white italic">
                      {listing.grossValue && Number(listing.grossValue) > 0
                        ? `€${Number(listing.grossValue).toLocaleString()}`
                        : "LA CERERE"}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  {listing.negotiable && (
                    <Tooltip
                      title={
                        <div className="font-mono tracking-tighter leading-tight">
                          <span className="text-white">STATUS:</span> PREȚUL ESTE <span className="text-blue-400">DESCHIS</span> PENTRU NEGOCIERI.
                        </div>
                      }
                      overlayInnerStyle={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: '11px',
                        lineHeight: '1.4',
                        color: '#22c55e',
                        backgroundColor: 'rgba(0, 20, 0, 0.9)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(34, 197, 94, 0.3)',
                        borderRadius: '4px',
                        padding: '12px',
                        textTransform: 'uppercase'
                      }}
                    >
                      <span className="text-emerald-400 font-mono font-bold text-[10px] uppercase border border-emerald-400/20 px-2 py-1 rounded cursor-help transition-all hover:bg-emerald-400/10 active:scale-95">
                        NEGOCIABIL
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>

              {/* 🟢 SELLER FINANCIAL BREAKDOWN HUD */}
              {isSeller && financialData && (
                <div className="flex flex-col gap-2 bg-slate-950/80 border border-slate-800 rounded-2xl p-4 mb-4">
                  <div className="flex justify-between items-center px-1 border-b border-slate-800/80 pb-2">
                    <span className="text-[9px] text-slate-400 font-mono uppercase tracking-wider">Model Taxare:</span>
                    <span className={`text-[8px] font-mono uppercase tracking-widest px-2 py-0.5 rounded font-black ${
                      financialData.isListingPlan ? "bg-blue-950 text-blue-400 border border-blue-900/50" : "bg-orange-950 text-orange-400 border border-orange-900/50"
                    }`}>
                      {financialData.chargingModelLabel}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 px-1 pt-1">
                    <div className="flex flex-col justify-center border-r border-slate-800/80 pr-2">
                      <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest leading-none mb-1">Preț Brut</span>
                      <span className="text-xs font-bold text-slate-300 font-mono truncate">{formatPrice(financialData.gross)}</span>
                    </div>

                    <div className="flex flex-col justify-center border-r border-slate-800/80 px-2">
                      <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest leading-none mb-1">
                        {financialData.isListingPlan ? "Comision" : `Comision (${(financialData.rate * 100).toFixed(0)}%)`}
                      </span>
                      <span className={`text-xs font-semibold font-mono truncate ${financialData.isListingPlan ? "text-slate-600" : "text-red-500"}`}>
                        {financialData.isListingPlan ? "0 €" : `-${formatPrice(financialData.commValue)}`}
                      </span>
                    </div>

                    <div className="flex flex-col justify-center pl-2">
                      <span className="text-[8px] text-slate-400 font-black uppercase tracking-widest leading-none mb-1">Payout Net</span>
                      <span className="text-sm font-black text-emerald-400 font-mono truncate">{formatPrice(financialData.net)}</span>
                    </div>
                  </div>
                </div>
              )}

              {!(isSeller && listing.verified === false) && (
                <button
                  disabled={isRequesting}
                  className="w-full py-4 bg-[#f26c15] hover:bg-[#df510d] disabled:opacity-75 text-white font-black rounded-xl shadow-lg shadow-black/40 transition-all flex items-center justify-center gap-3 active:scale-[0.98] uppercase tracking-widest text-sm"
                  onClick={handleAction}
                >
                  {isRequesting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Se trimite...
                    </>
                  ) : isSeller ? (
                    <>
                      <Settings size={18} /> Editează Anunțul
                    </>
                  ) : (
                    "Programează Vizionare"
                  )}
                </button>
              )}
            </div>

            <div className="space-y-10">
              <section>
                <h3 className="text-orange-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                  <Gauge size={14} /> Specificații Celulă
                </h3>
                {(hasFlightTime || hasProductionYear) && (
                  <div className={`grid ${hasFlightTime && hasProductionYear ? "grid-cols-2" : "grid-cols-1"} gap-4 mb-4`}>
                    {hasFlightTime && (
                      <div className="bg-slate-950/40 p-4 rounded-xl border border-white/5">
                        <TrendingUp size={16} className="text-slate-500 mb-2" />
                        <p className="text-[10px] font-bold text-slate-400 uppercase">Ore Totale</p>
                        <p className="text-xl font-black text-white">{listing.flightTime} <span className="text-xs text-slate-600">HRS</span></p>
                      </div>
                    )}
                    {hasProductionYear && (
                      <div className="bg-slate-950/40 p-4 rounded-xl border border-white/5">
                        <PlaneTakeoff size={16} className="text-slate-500 mb-2" />
                        <p className="text-[10px] font-bold text-slate-400 uppercase">An Fabricație</p>
                        <p className="text-xl font-black text-white">{listing.productionYear}</p>
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-1">
                  <DetailRow label="Tip Aeronavă" value={listing.resolvedTypeLabel} icon={Plane} />
                  <DetailRow label="Număr Locuri" value={listing.noSeats} icon={Users} />
                  <DetailRow label="Înmatriculare" value={listing.registration} icon={Hash} />
                  <DetailRow label="Data Înmatriculării" value={listing.registrationDate} icon={Calendar} />
                  <DetailRow label="Țară Înmatriculare" value={listing.resolvedRegCountryLabel} icon={Globe} />
                  <DetailRow label="Țară Origine" value={listing.resolvedOriginCountryLabel} icon={Globe} />
                  <DetailRow label="Stare" value={listing.resolvedStateLabel} icon={ShieldCheck} />
                  <DetailRow label="Sarcină Utilă" value={!isInvalidValue(listing.usefulLoad) ? `${listing.usefulLoad} kg` : null} icon={Weight} />
                  <DetailRow label="MTOM" value={!isInvalidValue(listing.mtom) ? `${listing.mtom} kg` : null} icon={Weight} />
                  <DetailRow label="Capacitate Rezervor" value={!isInvalidValue(listing.fuelCapacity) ? `${listing.fuelCapacity} L` : null} icon={Droplets} />
                  <DetailRow label="Tip Combustibil" value={listing.fuelType} icon={Droplets} />
                  <DetailRow label="Inspecție Anuală" value={listing.annualDue} icon={Calendar} />
                </div>
              </section>

              {listing.engines && listing.engines.length > 0 && (
                <section>
                  <h3 className="text-orange-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                    <Zap size={14} /> Grup Motopropulsor ({listing.noEngines || listing.engines.length})
                  </h3>
                  <div className="space-y-4">
                    {listing.engines.map((eng: any, i: number) => (
                      <div key={i} className="bg-slate-950/40 p-5 rounded-2xl border border-orange-500/10 space-y-2">
                        <div className="flex justify-between items-center mb-2 border-b border-white/5 pb-2">
                          <p className="text-[10px] font-bold text-blue-500 uppercase tracking-[0.2em]">Motor #{i + 1}</p>
                          {!isInvalidValue(eng.serialNumber) && (
                            <span className="text-[9px] font-mono text-slate-500">S/N: {eng.serialNumber}</span>
                          )}
                        </div>
                        <DetailRow label="Model" value={eng.model} />
                        <DetailRow label="Putere" value={!isInvalidValue(eng.power) ? `${eng.power} HP` : null} />
                        <DetailRow label="Ore Funcționare" value={!isInvalidValue(eng.functioningTime) ? `${eng.functioningTime} h` : null} />
                        <DetailRow label="Configurație" value={eng.configuration} />
                        <DetailRow label="Cilindri" value={eng.noCilinders} />
                        <DetailRow label="Capacitate Cilindrică" value={!isInvalidValue(eng.cilinderCapacity) ? `${eng.cilinderCapacity} cmc` : null} />
                        <DetailRow label="Răcire" value={eng.resolvedCoolingLabel} />
                        <DetailRow label="Stare Motor" value={eng.resolvedStateLabel} />
                        {!isInvalidValue(eng.additionalDetails) && (
                          <p className="text-[10px] text-slate-500 italic pt-2 border-t border-white/5 mt-2">{eng.additionalDetails}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {listing.propeller && (
                <section>
                  <h3 className="text-orange-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                    <Wind size={14} /> Elice
                  </h3>
                  <div className="bg-slate-950/40 p-5 rounded-2xl border border-white/5 space-y-1">
                    {!isInvalidValue(listing.propeller.serialNumber) && (
                      <div className="flex justify-between items-center mb-2 border-b border-white/5 pb-2">
                        <span className="text-[9px] font-mono text-slate-500">S/N: {listing.propeller.serialNumber}</span>
                      </div>
                    )}
                    <DetailRow label="Producător" value={listing.propeller.manufacturer} />
                    <DetailRow label="Tip" value={listing.resolvedPropellerLabel} />
                    <DetailRow label="Număr Pale" value={listing.propeller.noBlades} />
                    <DetailRow label="Rotație" value={listing.propeller.rotation} icon={RotateCcw} />
                    <DetailRow label="Ore Totale" value={!isInvalidValue(listing.propeller.flightTime) ? `${listing.propeller.flightTime} h` : null} />
                    {!isInvalidValue(listing.propeller.additionalDetails) && (
                      <p className="text-[10px] text-slate-500 italic pt-2 mt-2">{listing.propeller.additionalDetails}</p>
                    )}
                  </div>
                </section>
              )}

              {(hasAvionics || hasEquipment) && (
                <section>
                  <h3 className="text-orange-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                    <Info size={14} /> Avionică & Dotări
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {listing.avionics?.filter((item: string) => !isInvalidValue(item)).map((item: string, idx: number) => (
                      <span
                        key={`av-${idx}`}
                        className="px-2 py-1 bg-blue-600/10 text-blue-400 border border-blue-500/30 rounded text-[10px] font-bold uppercase tracking-tighter"
                      >
                        {item}
                      </span>
                    ))}
                    {listing.equipment?.filter((item: string) => !isInvalidValue(item)).map((item: string, idx: number) => (
                      <span key={`eq-${idx}`} className="px-2 py-1 bg-slate-800 text-slate-300 border border-white/10 rounded text-[10px] font-bold uppercase">
                        {item}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {hasSellerDescription && (
                <section className="pt-6 border-t border-slate-800">
                  <h3 className="text-slate-500 text-[10px] uppercase tracking-[0.3em] font-black mb-3">Descrierea Vânzătorului</h3>
                  <p className="text-slate-400 text-xs leading-relaxed italic whitespace-pre-wrap">
                    {listing.additionalDetails}
                  </p>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      {isLightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center animate-in fade-in duration-300"
          onClick={() => setIsLightboxOpen(false)}
        >
          <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center pointer-events-none z-50">
            <span className="text-white font-mono text-sm pointer-events-auto bg-white/5 px-3 py-1 rounded-full border border-white/10">
              {photoIndex + 1} / {images.length}
            </span>
            <button
              className="p-3 hover:bg-white/10 text-white rounded-full transition-colors pointer-events-auto"
              onClick={() => setIsLightboxOpen(false)}
            >
              <X size={32} />
            </button>
          </div>

          <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12">
            <button
              onClick={(e) => { e.stopPropagation(); setPhotoIndex((p) => (p === 0 ? images.length - 1 : p - 1)); }}
              className="absolute left-4 md:left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-all active:scale-90 z-10"
            >
              <ChevronLeft size={40} />
            </button>
            <div
              className="w-full max-w-[80vw] aspect-video rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10 bg-slate-900"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[photoIndex]}
                className="w-full h-full object-cover"
                alt="Gallery View"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://placehold.co/1200x800?text=Image+Unavailable';
                }}
              />
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setPhotoIndex((p) => (p + 1) % images.length); }}
              className="absolute right-4 md:right-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-all active:scale-90 z-10"
            >
              <ChevronRight size={40} />
            </button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {isConfirmOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-[6px] p-6">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-md overflow-hidden rounded-3xl bg-[#0F172A] border border-orange-500/20 shadow-[0_0_50px_rgba(242,108,21,0.1)]"
            >
              <div className="h-2 bg-gradient-to-r from-orange-600 via-amber-500 to-orange-600" />

              <div className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-500/10 text-orange-500 border border-orange-500/20">
                    <Calendar size={28} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black tracking-[0.2em] text-orange-500 uppercase">{dictViewing.title}</p>
                    <h3 className="text-2xl font-black text-white italic">{dictViewing.subtitle}</h3>
                  </div>
                </div>

                <div className="space-y-6 mb-8">
                  <p className="text-slate-300 text-xs leading-relaxed">
                    {dictViewing.description}
                  </p>

                  <div className="bg-slate-950 p-1.5 rounded-xl border border-slate-800 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setContactMethod("EMAIL")}
                      className={`flex-1 py-3 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${contactMethod === "EMAIL" ? "bg-orange-500 text-white shadow-lg" : "text-slate-500 hover:text-slate-400"}`}
                    >
                      📧 Pe E-mail
                    </button>
                    <button
                      type="button"
                      onClick={() => setContactMethod("PHONE")}
                      className={`flex-1 py-3 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${contactMethod === "PHONE" ? "bg-orange-500 text-white shadow-lg" : "text-slate-500 hover:text-slate-400"}`}
                    >
                      📞 Pe Telefon
                    </button>
                  </div>

                  <div className="rounded-xl bg-slate-950/50 border border-slate-900 p-4">
                    <ul className="space-y-3">
                      {dictViewing.info.map((line, index) => (
                        <li key={index} className="flex gap-3 text-[11px] text-slate-400 leading-tight">
                          <div className="mt-0.5 shrink-0 text-orange-500">✓</div>
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setIsConfirmOpen(false)}
                    className="flex-1 px-6 py-3 rounded-xl border border-slate-800 bg-slate-900/50 text-slate-400 text-xs font-black uppercase tracking-widest hover:text-white transition-colors"
                  >
                    {dictViewing.btnCancel}
                  </button>
                  <button
                    onClick={handleConfirmViewing}
                    className="flex-1 px-6 py-3 rounded-xl bg-orange-600 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-500 transition-all shadow-[0_0_20px_rgba(242,108,21,0.2)]"
                  >
                    {dictViewing.btnConfirm}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AircraftProductDetails;